import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface DashboardKPIs {
  totalSessions: number;
  activeAgents: number;
  avgResponseTime: number;
  successRate: number;
  tokensUsed: number;
}

export interface AgentStat {
  agentId: string;
  agentName: string;
  displayName?: string;
  sessionCount: number;
  successRate: number;
  avgResponseTime: number;
  tokensUsed: number;
}

export interface TaskAnalytics {
  totalTasks: number;
  successRate: number;
  avgDuration: number;
  topFailingTasks: {
    taskName: string;
    failureCount: number;
    lastError: string;
  }[];
}

interface DateRangeParams {
  startDate?: string;
  endDate?: string;
}

export const analyticsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDashboard: builder.query<DashboardKPIs, DateRangeParams | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.startDate) qs.set('start-date', params.startDate);
        if (params?.endDate) qs.set('end-date', params.endDate);
        const query = qs.toString();
        return `/analytics/dashboard${query ? `?${query}` : ''}`;
      },
      transformResponse: (raw: unknown) => normalizeKeys<DashboardKPIs>(raw),
      providesTags: ['Analytics'],
    }),
    getAgentBreakdown: builder.query<
      { agents: AgentStat[]; total: number },
      DateRangeParams | void
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.startDate) qs.set('start-date', params.startDate);
        if (params?.endDate) qs.set('end-date', params.endDate);
        const query = qs.toString();
        return `/analytics/agents${query ? `?${query}` : ''}`;
      },
      transformResponse: (raw: unknown) =>
        normalizeKeys<{ agents: AgentStat[]; total: number }>(raw),
      providesTags: ['Analytics'],
    }),
    getTaskAnalytics: builder.query<TaskAnalytics, DateRangeParams | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.startDate) qs.set('start-date', params.startDate);
        if (params?.endDate) qs.set('end-date', params.endDate);
        const query = qs.toString();
        return `/analytics/tasks${query ? `?${query}` : ''}`;
      },
      transformResponse: (raw: unknown) => normalizeKeys<TaskAnalytics>(raw),
      providesTags: ['Analytics'],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetAgentBreakdownQuery,
  useGetTaskAnalyticsQuery,
} = analyticsApi;
